import { useSupabaseClient } from '#imports'
import { selectWorkshopDate } from '~/helpers/selectWorkshopDate'
import type { Workshop, WorkshopDate, WorkshopSeatsAvailability } from '~/types/supabase'

export interface WorkshopWithDate extends Workshop {
  workshop_dates: WorkshopDate | null
}

export interface WorkshopWithSeats extends WorkshopWithDate {
  seats_availability?: WorkshopSeatsAvailability | null
}

export const useSupabaseWorkshops = () => {
  const client = useSupabaseClient()

  const getAllWorkshops = async () => {
    const { data, error } = await client
      .from('workshops')
      .select(`
        *,
        workshop_dates!inner (
          id,
          start_date,
          date_display,
          time,
          days,
          is_active,
          capacity
        )
      `)
      .eq('workshop_dates.is_active', true)
      .order('created_at', { ascending: false })

    return { workshops: (data?.map((workshop: any) => ({
      ...workshop,
      workshop_dates: selectWorkshopDate(workshop.workshop_dates)
    })) ?? null) as WorkshopWithDate[] | null, error }
  }

  const getWorkshopBySlug = async (slug: string) => {
    const { data, error } = await client
      .from('workshops')
      .select(`
        *,
        workshop_dates (
          id,
          start_date,
          date_display,
          time,
          days,
          is_active,
          capacity
        )
      `)
      .eq('slug', slug)
      .single()

    if (!data || error) {
      return { workshop: null, error }
    }

    const workshopData = data as any

    return {
      workshop: { ...workshopData, workshop_dates: selectWorkshopDate(workshopData.workshop_dates) } as WorkshopWithDate,
      error: null
    }
  }

  const getUpcomingWorkshops = async () => {
    const today = new Date().toISOString().split('T')[0]

    const { data, error } = await client
      .from('workshops')
      .select(`
        *,
        workshop_dates!inner (
          id,
          start_date,
          date_display,
          time,
          days,
          is_active,
          capacity
        )
      `)
      .eq('workshop_dates.is_active', true)
      .gte('workshop_dates.start_date', today)
      .order('created_at', { ascending: false })

    return { workshops: (data?.map((workshop: any) => ({
      ...workshop,
      workshop_dates: selectWorkshopDate(workshop.workshop_dates, today)
    })) ?? null) as WorkshopWithDate[] | null, error }
  }

  const getWorkshopsByType = async (type: string) => {
    const { data, error } = await client
      .from('workshops')
      .select(`
        *,
        workshop_dates (
          id,
          start_date,
          date_display,
          time,
          days,
          is_active,
          capacity
        )
      `)
      .eq('type', type)
      .order('created_at', { ascending: false })

    return { workshops: (data?.map((workshop: any) => ({
      ...workshop,
      workshop_dates: selectWorkshopDate(workshop.workshop_dates)
    })) ?? null) as WorkshopWithDate[] | null, error }
  }

  const getSeatsAvailability = async (workshopDateId: string) => {
    const { data, error } = await client
      .from('workshop_seats_availability')
      .select('*')
      .eq('workshop_date_id', workshopDateId)
      .single()

    return { availability: data as WorkshopSeatsAvailability | null, error }
  }

  const getAllWorkshopsWithSeats = async () => {
    // Get ALL workshops (including inactive ones) with left join to get workshops without dates too
    const { data, error } = await client
      .from('workshops')
      .select(`
        *,
        workshop_dates (
          id,
          start_date,
          date_display,
          time,
          days,
          is_active,
          capacity
        )
      `)
      .order('created_at', { ascending: false })

    if (error || !data) {
      return { workshops: null, error }
    }

    // Fetch seat availability for each workshop
    const workshopsWithSeats = await Promise.all(
      data.map(async (workshop: any) => {
        const dates: WorkshopDate[] = Array.isArray(workshop.workshop_dates)
          ? workshop.workshop_dates
          : []
        const workshopDate = selectWorkshopDate(dates)
        const workshopDateId = workshopDate?.id

        if (workshopDateId) {
          const { availability } = await getSeatsAvailability(workshopDateId)
          return {
            ...workshop,
            workshop_dates: workshopDate || null,
            seats_availability: availability
          } as WorkshopWithSeats
        }

        return {
          ...workshop,
          workshop_dates: null,
          seats_availability: null
        } as WorkshopWithSeats
      })
    )

    return { workshops: workshopsWithSeats, error: null }
  }

  return {
    getAllWorkshops,
    getWorkshopBySlug,
    getUpcomingWorkshops,
    getWorkshopsByType,
    getSeatsAvailability,
    getAllWorkshopsWithSeats
  }
}
